import { CSSProperties, useCallback, useMemo, useState } from 'react'

import styled from 'styled-components'

import {
  createSchedulePointMapping,
  deleteSchedulePointMapping,
} from '../../../../../api/adminBuildingFetches'
import { ProjectFloorSection } from '../../../../../api/types'
import { useBuildingContext } from '../../../../../contexts/buildingContext'
import { useNotifications } from '../../../../../contexts/notificationProvider'
import { ConfirmationModal } from '../../../../common/Confirmation/Confirmation'
import { LoadingIndicator } from '../../../../common/LoadingIndicator'
import {
  IGroup,
  PointGroupSelector,
} from '../../../../common/ViewSelector/components/PointGroupSelector/PointGroupSelector'
import { ViewSelector } from '../../../../common/ViewSelector/ViewSelector'
import { FloorPlanView } from '../../../building_page/components/FloorPlanView'
import { IMapPoint } from '../ManagePoints/ManagedMapPoint'

import { useManageSchedulesContext } from './ManageSchedulesContext'
import { ManualSequencePath } from './ManualSequencePath'
import { ConvexHullGrahamScan } from './ScheduleControls/graham_scan'
import { ScheduleControls } from './ScheduleControls/ScheduleControls'
import { getNextScan } from './ScheduleHelperFunctions'
import { SchedulePoint } from './SchedulePoint'

export const ScheduleFloorPlanView = () => {
  const {
    updateBuilding,
    updateFloor,
    state: buildingState,
  } = useBuildingContext()

  const { canCreateSchedulePointMapping, canDeleteSchedulePointMapping } =
    useManageSchedulesContext()

  const { scheduleData } = buildingState
  const { start_date, till_date, time, frequency } = scheduleData
  const { lessThan24HoursAway } = getNextScan(
    frequency,
    start_date,
    till_date,
    time,
  )

  const { addNotification } = useNotifications()

  const isNewSchedule = buildingState.scheduleId === 'new'

  const [pointControlsExpanded, setPointControlsExpanded] =
    useState<boolean>(true)
  const [scheduleSaving, setScheduleSaving] = useState<boolean>(false)
  const [confirmation, setConfirmation] = useState<boolean>(false)
  const [manualSequence, setManualSequence] = useState<number[]>([])
  const [manualSequenceDirty, setManualSequenceDirty] = useState<boolean>(false)
  const [isSequencingMode, setIsSequencingMode] = useState<boolean>(false)
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(
    null,
  )
  const [sections, setSections] = useState<ProjectFloorSection[]>([])

  const updateManualSequence = useCallback(
    (next: number[] | ((prev: number[]) => number[])) => {
      setManualSequence(prev => {
        const resolved = typeof next === 'function' ? next(prev) : next
        return resolved
      })
      setManualSequenceDirty(true)
    },
    [],
  )

  const loadManualSequence = useCallback((sequence: number[]) => {
    setManualSequence(sequence)
    setManualSequenceDirty(false)
  }, [])

  const markManualSequenceSaved = useCallback(() => {
    setManualSequenceDirty(false)
  }, [])

  const zoomIconStyle: CSSProperties = {
    position: 'absolute',
    right: '0px',
    zIndex: 2,
  }
  const zoomInIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '55px' }
  const zoomOutIconStyle: CSSProperties = { ...zoomIconStyle, bottom: '10px' }

  const sequenceOrderByPointId = useMemo(() => {
    const orderMap = new Map<number, number>()
    manualSequence.forEach((pointId, index) => {
      orderMap.set(pointId, index + 1)
    })
    return orderMap
  }, [manualSequence])

  const getPointSectionId = (point: IMapPoint): number | null => {
    if (point.project_floor_section == null) {
      return null
    }
    return Number(point.project_floor_section)
  }

  const isPointOnSchedule = (point: IMapPoint): boolean => {
    const selectedPoints = buildingState.floorData.selectedPoints
    return (
      selectedPoints.has(point.point_id) ||
      selectedPoints.has(Number(point.point_id))
    )
  }

  const scheduledPointsBySection = useMemo(() => {
    const counts = new Map<number, number>()
    buildingState.floorData.points.forEach((point: IMapPoint) => {
      if (!isPointOnSchedule(point)) {
        return
      }
      const sectionId = getPointSectionId(point)
      if (sectionId == null) {
        return
      }
      counts.set(sectionId, (counts.get(sectionId) ?? 0) + 1)
    })
    return counts
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isPointOnSchedule reads selectedPoints
  }, [buildingState.floorData.points, buildingState.floorData.selectedPoints])

  const isPointEligibleForSequence = (point: IMapPoint): boolean => {
    if (selectedSectionId == null || !isPointOnSchedule(point)) {
      return false
    }
    const pointSectionId = getPointSectionId(point)
    // New schedules may assign a section on create; allow unsectioned selected points.
    return (
      pointSectionId === Number(selectedSectionId) ||
      (isNewSchedule && pointSectionId == null)
    )
  }

  const isPointInSelectedSection = (point: IMapPoint): boolean => {
    if (selectedSectionId == null) {
      return false
    }
    const pointSectionId = getPointSectionId(point)
    return (
      pointSectionId === Number(selectedSectionId) ||
      (isNewSchedule && pointSectionId == null && isPointOnSchedule(point))
    )
  }

  const onToggleSequencingMode = () => {
    if (!isSequencingMode) {
      const currentCount =
        selectedSectionId == null
          ? 0
          : (scheduledPointsBySection.get(Number(selectedSectionId)) ?? 0)

      if (currentCount === 0 && scheduledPointsBySection.size > 0) {
        let bestSectionId: number | null = null
        let bestCount = 0
        scheduledPointsBySection.forEach((count, sectionId) => {
          if (count > bestCount) {
            bestSectionId = sectionId
            bestCount = count
          }
        })
        if (bestSectionId != null) {
          setSelectedSectionId(bestSectionId)
        }
      }
    }
    setIsSequencingMode(prev => !prev)
  }

  const onSequencePointClick = (point: IMapPoint) => {
    if (!isPointEligibleForSequence(point) || lessThan24HoursAway) {
      if (lessThan24HoursAway) {
        setConfirmation(true)
      }
      return
    }

    updateManualSequence(prev => {
      const lastPointId = prev[prev.length - 1]
      if (lastPointId === point.point_id) {
        return prev.slice(0, -1)
      }
      if (prev.includes(point.point_id)) {
        return prev
      }
      return [...prev, point.point_id]
    })
  }

  const performMapPointStateUpdates = (point: IMapPoint, addPoint: boolean) => {
    if (
      (addPoint && !canCreateSchedulePointMapping) ||
      (!addPoint && !canDeleteSchedulePointMapping)
    ) {
      return
    }

    const selectedPoints = buildingState.floorData.selectedPoints
    const scheduleMappings = new Map<string, Set<number>>(
      buildingState.scheduleMappings,
    )
    const convexHullCalculatorMappings = new Map<string, ConvexHullGrahamScan>(
      buildingState.convexHullCalculatorMappings,
    )
    const floorSet = scheduleMappings.get(buildingState.floorId)
    const floorConvexHullCalculator = convexHullCalculatorMappings.get(
      buildingState.floorId,
    )

    if (addPoint) {
      selectedPoints.add(point.point_id)
      floorSet?.add(point.point_id)
      floorConvexHullCalculator?.addPoint(point.point_id, point.x, point.y)
    } else {
      selectedPoints.delete(point.point_id)
      floorSet?.delete(point.point_id)
      floorConvexHullCalculator?.removePoint(point.point_id)
      updateManualSequence(prev => prev.filter(id => id !== point.point_id))
    }

    updateBuilding({
      scheduleMappings,
      convexHullCalculatorMappings,
    })

    updateFloor({
      selectedPoints: new Set(selectedPoints),
    })
  }

  const onAddPoint = async (point: IMapPoint, addPoint: boolean) => {
    if (!canCreateSchedulePointMapping) {
      return
    }

    try {
      const newMapping = await createSchedulePointMapping(
        buildingState.projectId,
        buildingState.scheduleId,
        point.point_id,
      )
      performMapPointStateUpdates(newMapping, addPoint)
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('createSchedulePointMapping==>>', err)
      addNotification('Error adding point to schedule', 'error')
    }
  }

  const onDeletePoint = async (point: IMapPoint, addPoint: boolean) => {
    if (!canDeleteSchedulePointMapping) {
      return
    }

    try {
      const deletedMapping = await deleteSchedulePointMapping(
        buildingState.projectId,
        buildingState.scheduleId,
        point.point_id,
      )
      performMapPointStateUpdates(deletedMapping, addPoint)
    } catch (err) {
      // eslint-disable-next-line no-console -- TODO: Replace with proper error logging
      console.log('deleteSchedulePointMapping==>>', err)
      addNotification('Error removing point from schedule', 'error')
    }
  }

  const onClickMapPoint = async (point: IMapPoint, addPoint: boolean) => {
    if (isSequencingMode) {
      onSequencePointClick(point)
      return
    }

    if (!lessThan24HoursAway) {
      if (!isNewSchedule) {
        if (addPoint) {
          onAddPoint(point, addPoint)
        } else {
          onDeletePoint(point, addPoint)
        }
      } else {
        performMapPointStateUpdates(point, addPoint)
      }
    } else {
      setConfirmation(true)
    }
  }

  const pointsForMap = useMemo(() => {
    const allPoints: IMapPoint[] = buildingState.floorData.points
    if (!isSequencingMode || selectedSectionId == null) {
      return allPoints
    }

    // In sequence mode, show every point in the selected section so membership is clear.
    // Only scheduled points in that section are clickable for ordering.
    return allPoints.filter(point => isPointInSelectedSection(point))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isPointInSelectedSection closes over section/schedule helpers
  }, [
    buildingState.floorData.points,
    buildingState.floorData.selectedPoints,
    isSequencingMode,
    selectedSectionId,
    isNewSchedule,
  ])

  const mapViewerChildren = [
    <ManualSequencePath
      key="manual-sequence-path"
      sequence={manualSequence}
      points={buildingState.floorData.points}
    />,
    ...pointsForMap.map((point: IMapPoint) => (
      <SchedulePoint
        key={point.point_id}
        point={point}
        x={Number(point.x)}
        y={Number(point.y)}
        onClick={() =>
          onClickMapPoint(
            point,
            !buildingState.floorData.selectedPoints.has(point.point_id),
          )
        }
        disabled={lessThan24HoursAway}
        sequenceOrder={sequenceOrderByPointId.get(point.point_id) ?? null}
        isSequencingMode={isSequencingMode}
        isEligibleForSequence={isPointEligibleForSequence(point)}
      />
    )),
  ]

  const onToggleGroupSelectorCheckbox = (
    group: IGroup,
    checked: boolean,
    groupPoints: number[],
  ) => {
    if (isSequencingMode) {
      return
    }

    groupPoints.forEach((pointId: number) => {
      const selectedPoint = buildingState.floorData.points.filter(
        (point: IMapPoint) => point.point_id === pointId,
      )[0]
      const actionRequired =
        buildingState.floorData.selectedPoints.has(pointId) !== checked

      if (selectedPoint && actionRequired) {
        onClickMapPoint(selectedPoint, checked)
      }
    })
  }

  const disabledOnToggleCheckbox = () => {
    setConfirmation(true)
  }

  return (
    <>
      {scheduleSaving && <LoadingIndicator />}
      {!scheduleSaving && (
        <FloorPlanView
          floorData={buildingState.floorData}
          hideDateRangeSelector
          mapViewerChildren={mapViewerChildren}
          zoomInIconStyle={zoomInIconStyle}
          zoomOutIconStyle={zoomOutIconStyle}
          showPoints={false}
        />
      )}

      <ViewSelectorContainer>
        <ViewSelector
          title="Schedule Controls"
          initialViewSelectorExpanded
          viewingExpanded={pointControlsExpanded}
          setViewingExpanded={setPointControlsExpanded}
          maxHeight={520}
        >
          <ScheduleControls
            setScheduleSaving={setScheduleSaving}
            manualSequence={manualSequence}
            setManualSequence={updateManualSequence}
            loadManualSequence={loadManualSequence}
            markManualSequenceSaved={markManualSequenceSaved}
            manualSequenceDirty={manualSequenceDirty}
            isSequencingMode={isSequencingMode}
            setIsSequencingMode={onToggleSequencingMode}
            selectedSectionId={selectedSectionId}
            setSelectedSectionId={setSelectedSectionId}
            sections={sections}
            setSections={setSections}
            scheduledPointsBySection={scheduledPointsBySection}
          />
        </ViewSelector>
        {(canCreateSchedulePointMapping || canDeleteSchedulePointMapping) && (
          <ViewSelector
            title="Groups"
            initialViewSelectorExpanded
            viewingExpanded={pointControlsExpanded}
            setViewingExpanded={setPointControlsExpanded}
            maxHeight={160}
          >
            <PointGroupSelector
              hideDelete
              hideCreateNewGroup
              parentOnToggleCheckbox={onToggleGroupSelectorCheckbox}
              disabledOnToggleCheckbox={disabledOnToggleCheckbox}
              disabled={lessThan24HoursAway || isSequencingMode}
            />
          </ViewSelector>
        )}
      </ViewSelectorContainer>

      <ConfirmationModal
        isOpen={confirmation}
        setIsOpen={setConfirmation}
        message="Points cannot be edited less than 24 hours away from the next scan"
        hideCancelButton
        confirmButtonAltText="Ok"
      />
    </>
  )
}

const ViewSelectorContainer = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`
